/**
 * Ana Sayfa — uygulamanın giriş ekranı.
 *
 * Amaç: kullanıcı açar açmaz (1) sistemin ne yaptığını, (2) hangi ekrana neden
 * gideceğini görsün. Modül kartları `config/navigation.jsx`'ten üretilir ve
 * kullanıcının ROLÜNE göre süzülür — yani herkes yalnız erişebildiği ekranları
 * görür. Yeni ekran eklendiğinde burası kendiliğinden güncellenir.
 *
 * Özet şeridi: uydurma/hesaplanmış metrik yok. Konveyör kartı `getDailyBSHStartStopResults`
 * REST ucundan, Asma/Toplama kartları ise o sayfaların da kullandığı canlı
 * WebSocket'lerden (`hang-socket-server`, `collect-socket-server`) besleniyor
 * — aktif siparişin anlık/hedef üretimi. Veri gelmeyen ya da uç hata veren
 * kutu tamamen gizlenir, 0 veya "-" gibi yanıltıcı bir değer basılmaz.
 */
import {
  ArrowRightOutlined,
  ClusterOutlined,
  InboxOutlined,
  ScheduleOutlined,
} from "@ant-design/icons";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import axios from "axios";
import { apiUrl, baseUrl } from "../config";
import { filterNavForRoles } from "../config/navigation";
import UserContext from "../Context/UserContext";

/**
 * Hero'nun koyu panelinde oturan cam efektli istatistik kartı.
 *
 * `ring` verilirse (değer + toplam) sayı bir "anlık/hedef" halka grafiğine
 * dönüşür — modül kartlarıyla (aşağıdaki beyaz, kenarlıklı kutular) aynı
 * göze çarpmasın diye kasıtlı olarak farklı bir görsel dil kullanılıyor.
 */
const HeroStat = ({ icon, label, value, ring }) => (
  <div className="flex flex-1 min-w-[190px] items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 backdrop-blur-sm">
    {ring ? (
      <div
        className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
        style={{
          background: `conic-gradient(#f1b634 ${ring.total > 0 ? (ring.value / ring.total) * 360 : 0}deg, rgba(255,255,255,.16) 0deg)`,
        }}
      >
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-ink-900 text-[12px] font-bold text-white">
          {value}
        </div>
      </div>
    ) : (
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-400/20 text-[17px] text-brand-300">
        {icon}
      </span>
    )}
    <div className="min-w-0">
      {!ring && <div className="text-[20px] font-bold leading-none text-white">{value}</div>}
      <div className={`truncate text-[12px] text-ink-200/70 ${ring ? "" : "mt-1"}`}>{label}</div>
    </div>
  </div>
);

const ModuleCard = ({ item }) => {
  const { t } = useTranslation();
  return (
    <Link
      to={item.path}
      className="group flex flex-col rounded-xl border border-line bg-surface p-4 shadow-card transition-all hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-raised"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-ink-900 text-[16px] text-brand-400 transition-colors group-hover:bg-brand-400 group-hover:text-ink-900">
          {item.icon}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-[14px] font-semibold text-content">
            <span className="truncate">{t(item.label)}</span>
            <ArrowRightOutlined className="shrink-0 text-[11px] text-content-faint opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100" />
          </div>
          <p className="mt-1 text-[12.5px] leading-relaxed text-content-muted">
            {t(item.desc)}
          </p>
        </div>
      </div>
    </Link>
  );
};

const Home = () => {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);

  const roles = user?.accountDetail?.idTokenClaims?.roles || [];
  const sections = useMemo(
    () => filterNavForRoles(roles),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roles.join("|")]
  );

  // Ana sayfanın kendisi kart olarak listelenmez.
  const cardSections = sections
    .map((s) => ({ ...s, items: s.items.filter((i) => i.key !== "home") }))
    .filter((s) => s.items.length > 0);

  /** "Hatta git" düğmesinin hedefi — her zaman Asma ekranı. */
  const firstPath = "/asma";

  // undefined: henüz veri gelmedi (kutu gizli) · null: uç/soket hata verdi (kutu gizli) · sayı: gösterilir.
  const [summary, setSummary] = useState({
    dailyOrders: undefined,
    asmaActual: undefined,
    asmaTarget: undefined,
    toplamaActual: undefined,
    toplamaTarget: undefined,
  });

  useEffect(() => {
    axios
      .get(`${apiUrl}/getDailyBSHStartStopResults`, {
        auth: {
          username: process.env.REACT_APP_USERNAME,
          password: process.env.REACT_APP_SECRET_KEY,
        },
      })
      .then((res) =>
        setSummary((s) => ({
          ...s,
          dailyOrders: Array.isArray(res.data) ? res.data.length : null,
        }))
      )
      .catch(() => setSummary((s) => ({ ...s, dailyOrders: null })));
  }, []);

  // Asma sayfasıyla aynı soket: aktif (başlamış, tamamlanmamış) siparişin anlık/hedef üretimi.
  useEffect(() => {
    const ws = new WebSocket(`${baseUrl}/ws/hang-socket-server/`);
    ws.onmessage = (e) => {
      try {
        const payload = JSON.parse(e.data);
        const active = payload?.data?.find(
          (item) => item?.fields?.is_started_asma === true && item?.fields?.is_completed_asma === false
        );
        if (active) {
          setSummary((s) => ({
            ...s,
            asmaActual: active.fields?.actual_production_asma ?? 0,
            asmaTarget: active.fields?.uretim ?? 0,
          }));
        }
      } catch {
        // HealthCheck pong'u gibi JSON olmayan çerçeveler yok sayılır.
      }
    };
    return () => ws.close();
  }, []);

  // Toplama sayfasıyla aynı soket: aktif siparişin anlık/hedef üretimi (gövde).
  useEffect(() => {
    const ws = new WebSocket(`${baseUrl}/ws/collect-socket-server/`);
    ws.onmessage = (e) => {
      try {
        const payload = JSON.parse(e.data);
        const active = payload?.data?.find((item) => item?.fields?.is_started_toplama === true);
        if (active) {
          setSummary((s) => ({
            ...s,
            toplamaActual: active.fields?.actual_production_toplama_govde ?? 0,
            toplamaTarget: active.fields?.uretim ?? 0,
          }));
        }
      } catch {
        // HealthCheck pong'u gibi JSON olmayan çerçeveler yok sayılır.
      }
    };
    return () => ws.close();
  }, []);

  const summaryTiles = [
    {
      key: "dailyOrders",
      icon: <ScheduleOutlined />,
      label: t("home.summary.dailyOrders"),
      value: summary.dailyOrders,
    },
    {
      key: "asmaProgress",
      icon: <ClusterOutlined />,
      label: t("home.summary.asmaProgress"),
      value: summary.asmaActual,
      ring:
        typeof summary.asmaActual === "number" && typeof summary.asmaTarget === "number"
          ? { value: summary.asmaActual, total: summary.asmaTarget }
          : null,
    },
    {
      key: "toplamaProgress",
      icon: <InboxOutlined />,
      label: t("home.summary.toplamaProgress"),
      value: summary.toplamaActual,
      ring:
        typeof summary.toplamaActual === "number" && typeof summary.toplamaTarget === "number"
          ? { value: summary.toplamaActual, total: summary.toplamaTarget }
          : null,
    },
  ].filter((tile) => typeof tile.value === "number");

  return (
    <div className="mx-auto max-w-[1480px] pb-6">
      {/* ---------- Hero ---------- */}
      <section className="animate-fade-up overflow-hidden rounded-2xl bg-ink-900 text-white shadow-raised">
        <div className="relative p-6 md:p-8">
          {/* Arka plan dokusu */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-[0.35]"
            style={{
              backgroundImage:
                "radial-gradient(circle at 85% 15%, rgba(241,182,52,.18), transparent 45%), linear-gradient(rgba(255,255,255,.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.04) 1px, transparent 1px)",
              backgroundSize: "auto, 44px 44px, 44px 44px",
            }}
          />
          <div className="relative max-w-3xl">
            <h2 className="text-[26px] font-semibold leading-[1.2] tracking-tight md:text-[32px]">
              {t("home.hero.title")}
            </h2>
            <p className="mt-3 text-[14px] leading-relaxed text-ink-200/80">
              {t("home.hero.subtitle")}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to={firstPath}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-400 px-4 py-2.5 text-[13.5px] font-semibold text-ink-900 transition-colors hover:bg-brand-300"
              >
                {t("home.hero.primaryCta")}
                <ArrowRightOutlined className="text-[11px]" />
              </Link>
            </div>
          </div>

          {/* ---------- Özet ---------- */}
          {summaryTiles.length > 0 && (
            <div className="relative mt-7 flex flex-wrap gap-3 border-t border-white/10 pt-6">
              {summaryTiles.map((tile) => (
                <HeroStat key={tile.key} icon={tile.icon} label={tile.label} value={tile.value} ring={tile.ring} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ---------- Modüller ---------- */}
      {cardSections.map((section) => (
        <section key={section.key} className="mt-7">
          <div className="mb-3 flex items-baseline gap-3">
            <h3 className="text-[15px] font-semibold tracking-tight text-content">
              {t(section.label)}
            </h3>
            <span className="hidden text-xs text-content-muted tablet:block">
              {t(section.desc)}
            </span>
            <span className="ml-auto text-2xs font-semibold uppercase tracking-label text-content-faint">
              {section.items.length} {t("home.modules.countSuffix")}
            </span>
          </div>
          <div className="grid gap-3 tablet:grid-cols-2 desktop:grid-cols-3">
            {section.items.map((item) => (
              <ModuleCard key={item.key} item={item} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export default Home;
